import { BatchedMesh, InstancedMesh, Matrix4, type Object3D } from 'three';
import type { SoundEmitter } from './spatialAudio';
import type { LifeRoute } from '../world/life/types';

/** Reuse the visible cast and gull matrices, rather than inventing offscreen crowds or animals. */
export function createActorSounds(root: Object3D): () => readonly SoundEmitter[] {
  const entries: { mesh: InstancedMesh | BatchedMesh; index: number; sound: SoundEmitter }[] = [];
  root.traverse((object) => {
    if (!(object instanceof InstancedMesh) && !(object instanceof BatchedMesh)) return;
    const routes = object.userData['lifeRoutes'] as readonly LifeRoute[] | undefined;
    const bird = object.name === 'bird-bodies';
    if (!bird && (!/^life-.*-body$/.test(object.name) || object.name === 'life-dog-body')) return;
    const count = object instanceof InstancedMesh ? object.count : (routes?.length ?? 0);
    for (let index = 0; index < count; index++) {
      if (routes?.[index]?.role === 'dog') continue;
      const horse = object.name === 'life-horse-body' || routes?.[index]?.role === 'horse';
      entries.push({
        mesh: object,
        index,
        sound: {
          id: `${object.name}-${index}`,
          kind: bird ? 'bird' : horse ? 'hooves' : 'footstep',
          at: [0, 0, 0],
          reach: bird ? 230 : horse ? 65 : 24,
          level: bird ? 0.1 : horse ? 0.18 : 0.075,
        },
      });
    }
  });
  const matrix = new Matrix4(),
    sounds: SoundEmitter[] = [];
  return () => {
    sounds.length = 0;
    for (const entry of entries) {
      const visible = entry.mesh.userData['lifeVisibility'] as readonly boolean[] | undefined;
      if (visible && !visible[entry.index]) continue;
      const walking = entry.mesh.userData['lifeWalking'] as readonly boolean[] | undefined;
      if (walking && !walking[entry.index]) continue;
      entry.mesh.getMatrixAt(entry.index, matrix);
      matrix.premultiply(entry.mesh.matrixWorld);
      const at = entry.sound.at as [number, number, number];
      at[0] = matrix.elements[12] ?? 0;
      at[1] = (matrix.elements[13] ?? 0) + (entry.sound.kind === 'bird' ? 0 : 0.15);
      at[2] = matrix.elements[14] ?? 0;
      sounds.push(entry.sound);
    }
    return sounds;
  };
}
